// User Behavior Tracking Service
// Tracks user job preferences, applications, and completions for ML-based recommendations

import { db } from '../config/firebase';
import { ref, set, get, update } from 'firebase/database';

const USER_BEHAVIOR_REF = 'userBehavior';
const COMPLETED_JOBS_REF = 'completedJobs';

/**
 * Track user behavior after applying for a job
 * Records: job skills, type, location, experience level for pattern recognition
 */
export const trackApplicationBehavior = async (userId, jobData) => {
  try {
    const behaviorRef = ref(db, `${USER_BEHAVIOR_REF}/${userId}`);
    const snapshot = await get(behaviorRef);
    const currentBehavior = snapshot.val() || {
      appliedCount: 0,
      preferredSkills: [],
      preferredTypes: [],
      preferredLocations: [],
      experienceLevels: [],
      averageMatchScore: 0,
      lastUpdated: new Date().toISOString(),
      applicationHistory: []
    };

    // Update behavior tracking
    const updatedBehavior = {
      ...currentBehavior,
      appliedCount: (currentBehavior.appliedCount || 0) + 1,
      preferredSkills: [
        ...new Set([
          ...(currentBehavior.preferredSkills || []),
          ...(jobData.skills || [])
        ])
      ].slice(0, 10),
      preferredTypes: [
        ...new Set([
          ...(currentBehavior.preferredTypes || []),
          jobData.type
        ])
      ],
      preferredLocations: [...new Set([
        ...(currentBehavior.preferredLocations || []),
        jobData.location
      ])].slice(0, 5),
      experienceLevels: [
        ...new Set([
          ...(currentBehavior.experienceLevels || []),
          jobData.experience
        ])
      ],
      lastUpdated: new Date().toISOString(),
      applicationHistory: [
        {
          jobId: jobData.id,
          title: jobData.title,
          appliedAt: new Date().toISOString(),
          matchScore: jobData.matchScore || 0
        },
        ...(currentBehavior.applicationHistory || []).slice(0, 49)
      ]
    };

    // Calculate average match score
    const totalScore = updatedBehavior.applicationHistory.reduce(
      (sum, app) => sum + (app.matchScore || 0),
      0
    );
    updatedBehavior.averageMatchScore = updatedBehavior.applicationHistory.length > 0
      ? Math.round((totalScore / updatedBehavior.applicationHistory.length) * 100) / 100
      : 0;

    await set(behaviorRef, updatedBehavior);

    console.log(
      '%c[User Behavior] Application tracked',
      'background: #2196F3; color: white; padding: 5px;',
      { userId, jobTitle: jobData.title }
    );

    return updatedBehavior;
  } catch (error) {
    console.error('Error tracking application behavior:', error);
    throw error;
  }
};

/**
 * Track job completion
 */
export const trackJobCompletion = async (userId, jobId, jobData) => {
  try {
    const completedRef = ref(db, `${COMPLETED_JOBS_REF}/${userId}/${jobId}`);
    
    await set(completedRef, {
      jobId,
      title: jobData.title,
      skills: jobData.skills || [],
      type: jobData.type,
      location: jobData.location,
      experience: jobData.experience,
      completedAt: new Date().toISOString(),
      matchScore: jobData.matchScore || 0
    });

    // Update behavior - add to completed skills for recommendations
    const behaviorRef = ref(db, `${USER_BEHAVIOR_REF}/${userId}`);
    const snapshot = await get(behaviorRef);
    const currentBehavior = snapshot.val() || {};

    const updatedBehavior = {
      ...currentBehavior,
      completedCount: (currentBehavior.completedCount || 0) + 1,
      completedSkills: [
        ...new Set([
          ...(currentBehavior.completedSkills || []),
          ...(jobData.skills || [])
        ])
      ].slice(0, 15),
      lastJobCompleted: new Date().toISOString()
    };

    await update(behaviorRef, updatedBehavior);

    console.log(
      '%c[User Behavior] Job completion tracked',
      'background: #4CAF50; color: white; padding: 5px;',
      { userId, jobTitle: jobData.title }
    );

    return true;
  } catch (error) {
    console.error('Error tracking job completion:', error);
    throw error;
  }
};

/**
 * Get user behavior profile
 */
export const getUserBehavior = async (userId) => {
  try {
    const behaviorRef = ref(db, `${USER_BEHAVIOR_REF}/${userId}`);
    const snapshot = await get(behaviorRef);
    return snapshot.val() || null;
  } catch (error) {
    console.error('Error fetching user behavior:', error);
    return null;
  }
};

/**
 * Get completed jobs for a user
 */
export const getUserCompletedJobs = async (userId) => {
  try {
    const completedRef = ref(db, `${COMPLETED_JOBS_REF}/${userId}`);
    const snapshot = await get(completedRef);
    const completedJobs = snapshot.val() || {};
    return Object.values(completedJobs);
  } catch (error) {
    console.error('Error fetching completed jobs:', error);
    return [];
  }
};
